import { useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { createUseStyles } from 'react-jss'
import { faCheck } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Colors } from '../../constants/colors'
import { OpenShiftRecipient } from "../../redux/types"
import ListItemCard from "../ListItemCard"
import Pagination from '../Pagination'

interface Props {
    recipients: OpenShiftRecipient[]
    pageIndex: number
    pageSize: number
    onPageChange: (pageIndex: number) => void
}

const OpenShiftRecipientList = ({ recipients, pageIndex, pageSize, onPageChange } : Props) => {
    const styles = useStyles()
    const { t } = useTranslation(['common', 'openShifts']);

    const itemsOnCurrentPage = useMemo(() => {
        const firstPageIndex = (pageIndex - 1) * pageSize;
        const lastPageIndex = firstPageIndex + pageSize;
        return recipients.slice(firstPageIndex, lastPageIndex);
    }, [recipients, pageIndex, pageSize])

    const renderHeader = () => {
        return (
            <ListItemCard className={styles.listItemContainer}>
                <div className={styles.idContainer}>
                    <strong>{t('id', {ns: 'common'})}</strong>
                </div> 
                <div className={styles.nameContainer}>
                    <strong>{t('name', {ns: 'common'})}</strong> 
                </div>
                <div className={styles.booleanColumnContainer}>
                    <strong>{t('openShiftIsNew', {ns: 'openShifts'})}</strong>
                </div>
                <div className={styles.booleanColumnContainer}>
                    <strong>{t('scheduleConflict', {ns: 'openShifts'})}</strong>
                </div>
                <div className={styles.booleanColumnContainer}>
                    <strong>{t('restingTimeRuleViolation', {ns: 'openShifts'})}</strong>
                </div>
            </ListItemCard>
        )    
    }

    const renderBooleanValue = (value: boolean, color: string) => {
        return value
            ? <FontAwesomeIcon icon={faCheck} color={color} />
            : null
    }

    const renderListItems = () => {
        return itemsOnCurrentPage?.map((row, index) => {
            return (
                <ListItemCard key={`row${index}`} className={styles.listItemContainer} index={index}>
                    <div className={styles.idContainer}>
                        {row.externalId}
                    </div> 
                    <div className={styles.nameContainer}>
                        {row.name}
                    </div>
                    <div className={styles.booleanColumnContainer}>
                        {renderBooleanValue(row.openShiftIsNew, Colors.euro_600)}
                    </div>
                    <div className={styles.booleanColumnContainer}>
                        {renderBooleanValue(row.scheduleConflict, Colors.rubel_300)}
                    </div>
                    <div className={styles.booleanColumnContainer}>
                        {renderBooleanValue(row.restingTimeRuleViolation, Colors.rubel_300)}
                    </div>
                </ListItemCard>
            ) 
         })
    }

    return (
        <>
            <div className={styles.infoMessage}>
                {t('recipientInfoMessage', {ns: 'openShifts'})}                
            </div>
           <div className={styles.container}>
               {renderHeader()}
               {renderListItems()}
           </div>
           <div className={styles.pagination}>
            <Pagination
                pageIndex={pageIndex}
                itemCount={recipients.length}
                pageSize={pageSize}
                siblingCount={1}
                onPageChange={onPageChange}
            />
            </div>
        </>
    )
}

export default OpenShiftRecipientList

const useStyles = createUseStyles({
    container: {
        width: '100%',
        overflowX: 'auto',
    },
    listItemContainer: {
        display: 'flex',
        flex: 1,
        flexDirection: 'row',
        alignContent: 'center',
        justifyContent: 'space-between',
    },
    idContainer: {
        width: 150
    },
    nameContainer: {
        flex: 1,
    },
    booleanColumnContainer: {
        flexShrink: 0,
        width: 150,
        display: 'flex',
        justifyContent: 'center',
    },
    pagination: {
        marginTop: 10,
        display: 'flex',
        justifyContent: 'center'
    },
    infoMessage: {
        marginBottom: 10
    }
  })
